import React from "react";
import { orderBy } from "lodash";
import classNames from "classnames";
import { getQuantityToDisplay } from "./util";
import { useCopies } from "shared/hooks/useCopies";
import { getFormattedCurrency } from "shared/utils";
import ApiPurchaseOrder from "shared/model/ApiPurchaseOrder";
import ApiPurchaseOrderLine from "shared/model/ApiPurchaseOrderLine";
import { BulkTicketOrMeetingsPurchaseHistoryArgsType } from "./type";
import useBulkTicketOrMeetingsPurchaseHistoryService from "./service";
import ApiPurchaseOrderAdjustment from "shared/model/ApiPurchaseOrderAdjustment";
import { getDateTimeToDisplay } from "apps/myExperience/components/purchaseHistory/util";
import PurchaseOrderLineAdjustmentType from "shared/model/PurchaseOrderLineAdjustmentType";

export default function BulkTicketsOrMeetingsPurchaseHistory(props: BulkTicketOrMeetingsPurchaseHistoryArgsType) {
  const copies = useCopies();
  const service = useBulkTicketOrMeetingsPurchaseHistoryService(props);
  if (service.completedPurchaseHistoryList?.length === 0) {
    return null;
  }
  return (
    <React.Fragment>
      {service?.completedPurchaseHistoryList?.length ? (
        <div className="inventoryPurchase_wrapper">
          <b className="inventoryPurchase_title">{copies.get("transaction.history")}</b>
          <table className="inventoryPurchase_table">
            <thead className="inventoryPurchase_thead">
              <tr className="inventoryPurchase_thead--row">
                <th className="inventoryPurchase_thead--th">{copies.get("date.time")}</th>
                <th className="inventoryPurchase_thead--th">{copies.get("meeting.purchased.by")}</th>
                <th className="inventoryPurchase_thead--th">{copies.get("quantity.short")}</th>
                <th className="inventoryPurchase_thead--th">{copies.get("rate")}</th>
                <th className="inventoryPurchase_thead--th">{copies.get("total.amount")}</th>
              </tr>
            </thead>
            <tbody className="inventoryPurchase_tbody">
              {orderBy(service.completedPurchaseHistoryList, "createdDate.dateTime", "desc")?.map(purchaseObj => (
                <BulkTicketsOrMeetingsPurchaseOrderRow purchaseObj={purchaseObj} key={purchaseObj.purchaseOrderId} />
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </React.Fragment>
  );
}

function BulkTicketsOrMeetingsPurchaseOrderRow({ purchaseObj }: { purchaseObj: ApiPurchaseOrder }) {
  const copies = useCopies();
  const dateToDisplay = React.useMemo(() => {
    return getDateTimeToDisplay(purchaseObj.createdDate);
  }, [purchaseObj.createdDate]);

  return (
    <React.Fragment>
      {purchaseObj.lines.map((lineObj: ApiPurchaseOrderLine) => {
        let quantityToDisplay = getQuantityToDisplay(lineObj.quantity, PurchaseOrderLineAdjustmentType.ADD, copies);
        return (
          <tr key={lineObj.purchaseOrderLineId} className="inventoryPurchase_tbody--row">
            <td className="inventoryPurchase_tbody--td">{dateToDisplay}</td>
            <td className="inventoryPurchase_tbody--td">
              {purchaseObj.invoicePaymentRequest?.invoicePayerEmail ?? purchaseObj.transactionBy}
            </td>
            <td
              className={classNames(
                "inventoryPurchase_tbody--td",
                PurchaseOrderLineAdjustmentType.ADD
                  ? "inventoryPurchase_qty--completed"
                  : "inventoryPurchase_qty--cancelled"
              )}>
              {quantityToDisplay}
            </td>
            <td className="inventoryPurchase_tbody--td inventoryPurchase_rate">
              {getFormattedCurrency(lineObj.unitPriceAmount)}
            </td>
            <td className="inventoryPurchase_tbody--td inventoryPurchase_totalAmount">
              {getFormattedCurrency(lineObj.totalAmount)}
            </td>
          </tr>
        );
      })}
      {orderBy(purchaseObj.purchaseOrderAdjustments, "createdDate.dateTime", "desc").map(orderAdjustment => (
        <BulkTicketsOrMeetingsPurchaseOrderAdjustmentRow
          purchaseOrderAdjustmentObj={orderAdjustment}
          key={orderAdjustment.id}
        />
      ))}
    </React.Fragment>
  );
}

function BulkTicketsOrMeetingsPurchaseOrderAdjustmentRow({
  purchaseOrderAdjustmentObj
}: {
  purchaseOrderAdjustmentObj: ApiPurchaseOrderAdjustment;
}) {
  const copies = useCopies();
  const dateToDisplay = React.useMemo(() => {
    return getDateTimeToDisplay(purchaseOrderAdjustmentObj.createdDate);
  }, [purchaseOrderAdjustmentObj.createdDate]);

  return (
    <React.Fragment>
      {purchaseOrderAdjustmentObj.purchaseOrderLineAdjustments
        .filter(i => i.quantity > 0)
        .map(lineAdjustmentObj => {
          let quantityToDisplay = getQuantityToDisplay(
            lineAdjustmentObj.quantity,
            lineAdjustmentObj.purchaseOrderlineAdjustmentType,
            copies
          );
          return (
            <tr key={lineAdjustmentObj.purchaseOrderLineId} className="inventoryPurchase_tbody--row">
              <td className="inventoryPurchase_tbody--td">{dateToDisplay}</td>
              <td className="inventoryPurchase_tbody--td">{purchaseOrderAdjustmentObj.transactionDoneBy}</td>
              <td
                className={classNames(
                  "inventoryPurchase_tbody--td",
                  lineAdjustmentObj.purchaseOrderlineAdjustmentType === PurchaseOrderLineAdjustmentType.ADD
                    ? "inventoryPurchase_qty--completed"
                    : "inventoryPurchase_qty--cancelled"
                )}>
                {quantityToDisplay}
              </td>
              <td className="inventoryPurchase_tbody--td">{copies.get("empty.cell")}</td>
              <td className="inventoryPurchase_tbody--td">{copies.get("empty.cell")}</td>
            </tr>
          );
        })}
    </React.Fragment>
  );
}
